// ============================================================================
// EXTEND BLOCK MODAL
// ============================================================================
// One-click extension of a BLOCKED booking's expiry. Reads BLOCKING_POLICY
// config (extension_hours / max_block_hold_hours / max_extensions) and shows
// the cashier exactly what will happen — current expiry, the +N hours grant,
// the projected new expiry, and any clamping at the max-hold cap. The single
// action button calls POST /Booking/{id}/block/extend; the server re-validates
// and recomputes, so this modal is purely informational + one-tap submit.

import React, { useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { bookingService } from 'api/services';
import { useBlockingPolicy } from 'hooks/api/useConfig';
import notificationService from 'services/notificationService';
import type { Booking } from 'api/types/booking.types';

interface ExtendBlockModalProps {
  isOpen: boolean;
  booking: Booking | null;
  onClose: () => void;
  onExtended: () => void;
}

const fmt = (d: Date): string =>
  d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });

const ExtendBlockModal: React.FC<ExtendBlockModalProps> = ({
  isOpen, booking, onClose, onExtended,
}) => {
  const { config: policy, refetch: refetchPolicy } = useBlockingPolicy(booking?.propertyId);
  const [submitting, setSubmitting] = useState(false);

  // Refetch policy on every open — config can change at any time.
  useEffect(() => {
    if (isOpen && booking?.propertyId) refetchPolicy(booking.propertyId);
  }, [isOpen, booking?.propertyId, refetchPolicy]);

  // Derived projection for the read-only summary block.
  const projection = useMemo(() => {
    if (!isOpen || !booking || !booking.expiresAt || !policy) return null;
    const currentExpiry = new Date(booking.expiresAt);
    const now           = new Date();
    const cap           = new Date(now.getTime() + policy.maxBlockHoldHours * 3600 * 1000);
    const proposed      = new Date(currentExpiry.getTime() + policy.extensionHours * 3600 * 1000);
    const newExpiry     = proposed.getTime() > cap.getTime() ? cap : proposed;
    const clamped       = proposed.getTime() > cap.getTime();
    const used          = booking.extensionCount ?? 0;
    const remaining     = Math.max(0, policy.maxExtensions - used - 1);
    const atCap         = currentExpiry.getTime() >= cap.getTime();
    const reachedLimit  = used >= policy.maxExtensions;
    return { currentExpiry, now, cap, newExpiry, clamped, used, remaining, atCap, reachedLimit };
  }, [isOpen, booking, policy]);

  if (!isOpen || !booking) return null;

  const blockGuard = (() => {
    if (!policy) return { disabled: true, reason: 'Loading policy…' };
    if (!policy.blockExtensionAllowed)
      return { disabled: true, reason: 'Block extensions are disabled in policy.' };
    if (!projection)
      return { disabled: true, reason: 'Loading…' };
    if (projection.reachedLimit)
      return { disabled: true, reason: `Already extended the maximum number of times (${projection.used}/${policy.maxExtensions}).` };
    if (projection.atCap)
      return { disabled: true, reason: `Block already at maximum hold (${policy.maxBlockHoldHours}h from now); cannot extend further.` };
    return { disabled: false, reason: '' };
  })();

  const handleExtend = async () => {
    setSubmitting(true);
    try {
      await bookingService.extendBlock(booking.bookingId);
      notificationService.toast.success(
        `Block extended for "${booking.bookingNumber}".`
      );
      onExtended();
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message ||
        'Failed to extend block.';
      notificationService.toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const ROW: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '180px 1fr',
    gap: '0.75rem',
    fontSize: '0.875rem',
    padding: '0.25rem 0',
  };
  const LABEL: React.CSSProperties = { color: 'var(--text-secondary)' };
  const VALUE: React.CSSProperties = { color: 'var(--text-primary)', fontWeight: 500 };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-sm">
        {submitting && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Extending…</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Extend Block — {booking.bookingNumber}</h2>
          <button className="modal-close" onClick={onClose} disabled={submitting}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          {/* Current state */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={ROW}>
              <span style={LABEL}>Current expiry</span>
              <span style={VALUE}>
                {projection ? fmt(projection.currentExpiry) : '—'}
              </span>
            </div>
            <div style={ROW}>
              <span style={LABEL}>Extensions used</span>
              <span style={VALUE}>
                {policy ? `${projection?.used ?? 0} of ${policy.maxExtensions}` : '—'}
              </span>
            </div>
            <div style={ROW}>
              <span style={LABEL}>Extension grants</span>
              <span style={VALUE}>
                {policy ? `+${policy.extensionHours} hours per extension` : '—'}
              </span>
            </div>
            <div style={ROW}>
              <span style={LABEL}>Maximum hold</span>
              <span style={VALUE}>
                {policy ? `now + ${policy.maxBlockHoldHours}h` : '—'}
              </span>
            </div>
          </div>

          {/* Projected after-state */}
          {projection && policy?.blockExtensionAllowed && !projection.reachedLimit && !projection.atCap && (
            <div
              style={{
                padding: '0.75rem 1rem',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                marginBottom: '0.75rem',
              }}
            >
              <div style={{ ...ROW, padding: 0 }}>
                <span style={LABEL}>After this extension</span>
                <span style={{ ...VALUE, color: '#16a34a' }}>
                  {fmt(projection.newExpiry)}
                </span>
              </div>
              <div style={{ ...ROW, padding: 0 }}>
                <span style={LABEL}>Remaining after</span>
                <span style={VALUE}>
                  {projection.remaining} of {policy.maxExtensions} extensions
                </span>
              </div>
            </div>
          )}

          {/* Clamp warning (would-have-exceeded) */}
          {projection?.clamped && !projection.atCap && (
            <div
              style={{
                padding: '0.625rem 0.875rem',
                background: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '6px',
                color: '#78350f',
                fontSize: '0.8125rem',
                marginBottom: '0.75rem',
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'flex-start',
              }}
            >
              <IconSystem.alert size={16} />
              <span>
                This extension would exceed the {policy?.maxBlockHoldHours}h max-hold limit.
                The new expiry has been capped at <strong>{fmt(projection.newExpiry)}</strong>.
              </span>
            </div>
          )}

          {/* Hard-block reason */}
          {blockGuard.disabled && blockGuard.reason && (
            <div
              style={{
                padding: '0.625rem 0.875rem',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                color: 'var(--text-secondary)',
                fontSize: '0.8125rem',
                fontStyle: 'italic',
              }}
            >
              {blockGuard.reason}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleExtend}
            disabled={submitting || blockGuard.disabled}
            title={blockGuard.disabled ? blockGuard.reason : undefined}
          >
            <IconSystem.clock size={16} />
            {policy ? `Extend +${policy.extensionHours}h` : 'Extend'}
          </button>
        </div>
      </div>
    </>
  );
};

export default ExtendBlockModal;
